import React from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { 
  TrendingUp, 
  Target, 
  Lightbulb, 
  MapPin, 
  Zap,
  Star,
  Navigation2,
  Shield
} from 'lucide-react';

interface QuickPromptsProps {
  selectedAgent: string;
  onPromptSelect: (prompt: string) => void;
  userContext?: {
    loadedScore?: number;
    careerStage?: string;
    strengths?: string[];
    growthAreas?: string[];
  };
}

const AGENT_PROMPTS = {
  'loaded-bot': [
    {
      icon: Star,
      title: "Career Health Check",
      prompt: "Can you give me a comprehensive assessment of my current career position and what areas I should focus on?"
    },
    {
      icon: Target,
      title: "Goal Setting",
      prompt: "Help me set SMART career goals for the next 6 months based on my current situation."
    },
    {
      icon: Lightbulb,
      title: "Skill Development",
      prompt: "What are the most important skills I should develop to advance in my career?"
    },
    {
      icon: MapPin,
      title: "Career Path",
      prompt: "What are some potential career paths I could explore given my background and interests?"
    }
  ],
  'career-navigator': [
    {
      icon: Navigation2,
      title: "Strategic Planning",
      prompt: "Help me create a 5-year strategic career plan with specific milestones and objectives."
    },
    {
      icon: TrendingUp,
      title: "Industry Analysis",
      prompt: "What are the current trends in my industry and how should I position myself?"
    },
    {
      icon: Target,
      title: "Opportunity Assessment",
      prompt: "I'm considering a career move. Can you help me evaluate this opportunity strategically?"
    },
    {
      icon: Zap,
      title: "Competitive Advantage",
      prompt: "How can I build and maintain a competitive advantage in my field?"
    }
  ],
  'score-booster': [
    {
      icon: TrendingUp,
      title: "Score Analysis",
      prompt: "Analyze my current Loaded Score and provide specific recommendations for improvement."
    },
    {
      icon: Target,
      title: "Skill Gaps",
      prompt: "What are my biggest skill gaps and how can I address them systematically?"
    },
    {
      icon: Star,
      title: "Performance Optimization",
      prompt: "How can I optimize my performance in each of the five assessment dimensions?"
    },
    {
      icon: Lightbulb,
      title: "Quick Wins",
      prompt: "What are some quick wins I can achieve to boost my professional score in the next 30 days?"
    }
  ],
  'future-proof-advisor': [
    {
      icon: Shield,
      title: "Resilience Assessment",
      prompt: "How resilient is my current career position to industry disruption and change?"
    },
    {
      icon: TrendingUp,
      title: "Future Trends",
      prompt: "What emerging trends should I be preparing for in my industry?"
    },
    {
      icon: Zap,
      title: "Adaptability Plan",
      prompt: "Help me create a plan to become more adaptable and future-ready in my career."
    },
    {
      icon: Lightbulb,
      title: "Continuous Learning",
      prompt: "What should I be learning now to stay relevant in the future job market?"
    }
  ]
};

export const QuickPrompts: React.FC<QuickPromptsProps> = ({ 
  selectedAgent, 
  onPromptSelect, 
  userContext 
}) => {
  const prompts = AGENT_PROMPTS[selectedAgent as keyof typeof AGENT_PROMPTS] || AGENT_PROMPTS['loaded-bot'];

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-text-primary">Quick Start Prompts</h3>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {prompts.map((prompt, index) => {
          const IconComponent = prompt.icon;
          
          return (
            <Card
              key={index}
              className="p-4 cursor-pointer transition-all duration-300 hover:shadow-lg hover:scale-[1.02] border border-border/30 hover:border-primary/30 bg-card-bg hover:bg-card-hover"
              onClick={() => onPromptSelect(prompt.prompt)}
            >
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-lg bg-primary/10">
                  <IconComponent className="w-5 h-5 text-primary" />
                </div>
                <div className="flex-1">
                  <h4 className="font-medium text-text-primary mb-1">{prompt.title}</h4>
                  <p className="text-sm text-text-muted line-clamp-2">{prompt.prompt}</p>
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Contextual prompts based on user data */}
      {userContext?.loadedScore && (
        <div className="space-y-3">
          <h4 className="text-md font-medium text-text-primary">Personalized Suggestions</h4>
          
          <Card
            className="p-4 cursor-pointer transition-all duration-300 hover:shadow-lg hover:scale-[1.02] border border-primary/20 bg-gradient-to-r from-primary/5 to-secondary/5"
            onClick={() => onPromptSelect(`My current Loaded Score is ${userContext.loadedScore}. What specific actions can I take to improve my score and advance to the next career stage?`)}
          >
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-primary/20">
                <TrendingUp className="w-5 h-5 text-primary" />
              </div>
              <div className="flex-1">
                <h4 className="font-medium text-text-primary mb-1">Score Improvement Plan</h4>
                <p className="text-sm text-text-muted">
                  Get specific recommendations based on your current score of {userContext.loadedScore}
                </p>
              </div>
            </div>
          </Card>

          {userContext.growthAreas && userContext.growthAreas.length > 0 && (
            <Card
              className="p-4 cursor-pointer transition-all duration-300 hover:shadow-lg hover:scale-[1.02] border border-orange-500/20 bg-gradient-to-r from-orange-500/5 to-yellow-500/5"
              onClick={() => onPromptSelect(`I need to improve in these areas: ${userContext.growthAreas?.join(', ')}. Can you create a development plan for me?`)}
            >
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-lg bg-orange-500/20">
                  <Target className="w-5 h-5 text-warning" />
                </div>
                <div className="flex-1">
                  <h4 className="font-medium text-text-primary mb-1">Growth Area Focus</h4>
                  <p className="text-sm text-text-muted">
                    Develop a plan for your growth areas: {userContext.growthAreas.slice(0, 2).join(', ')}
                    {userContext.growthAreas.length > 2 && ` and ${userContext.growthAreas.length - 2} more`}
                  </p>
                </div>
              </div>
            </Card>
          )}
        </div>
      )}
    </div>
  );
};